/**
 * Accountant Export Pack — Phase 11.
 *
 * Pure string builders for QuickBooks-compatible exports. No DOM, no I/O.
 * The page wraps each output in a Blob and triggers a download.
 *
 * Formats produced:
 *   1. IIF (QuickBooks Desktop)   — Chart of Accounts + General Journal txns
 *   2. CSV pack                    — Accounts / JEs / Customers / Vendors /
 *                                    Bills / Invoices / Payments
 *
 * IIF reference: https://quickbooks.intuit.com/learn-support/en-us/help-article/
 * import-export-data/format-iif-files/L8aSttRRJ_US_en_US
 */
import type { JournalEntry, GLAccount } from "../glTypes";
import type { Bill, BillPayment } from "../ap/apTypes";
import type { Vendor } from "@/data/VendorPOContext";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** IIF uses TAB delimiters and CRLF line endings. */
const TAB = "\t";
const CRLF = "\r\n";

/** Strip TAB / CRLF from any string going into an IIF/CSV cell. */
const cleanCell = (v: string | number | undefined | null): string => {
  if (v === undefined || v === null) return "";
  return String(v).replace(/[\t\r\n]+/g, " ").trim();
};

/** RFC-4180 CSV cell escaping. */
const csvCell = (v: string | number | undefined | null): string => {
  if (v === undefined || v === null) return "";
  const s = String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** IIF date format: M/D/YYYY. Use UTC components to avoid local-TZ off-by-one. */
const iifDate = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
};

/** Money for IIF: signed, 2 decimal, no thousand sep. Debit positive, credit negative. */
const iifAmount = (n: number, side: "debit" | "credit"): string => {
  const v = side === "credit" ? -n : n;
  return v.toFixed(2);
};

/** "1100 · Accounts Receivable" — QB convention "number · name". */
const accountFullName = (acc: GLAccount): string =>
  cleanCell(`${acc.number} · ${acc.name}`);

/** Map our GLAccountType → IIF ACCNTTYPE codes. */
const iifAccountType = (acc: GLAccount): string => {
  switch (acc.type) {
    case "asset":
      if (acc.subtype === "cash" || acc.subtype === "unreconciled_cash") return "BANK";
      if (acc.subtype === "accounts_receivable") return "AR";
      if (acc.subtype === "fixed_asset") return "FIXASSET";
      if (acc.subtype === "wip") return "OCASSET";
      return "OCASSET";
    case "liability":
      if (acc.subtype === "accounts_payable") return "AP";
      if (acc.subtype === "credit_card_payable") return "CCARD";
      if (acc.subtype === "deferred_revenue") return "OCLIAB";
      return "OCLIAB";
    case "equity": return "EQUITY";
    case "revenue": return "INC";
    case "expense":
      if (acc.subtype === "cost_of_material" || acc.subtype === "cost_of_labor") return "COGS";
      return "EXP";
  }
};

const inRange = (date: string, from?: string, to?: string): boolean => {
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
};

// ─────────────────────────────────────────────────────────────────────────────
// IIF Export
// ─────────────────────────────────────────────────────────────────────────────

export interface IifExportOptions {
  fromDate?: string; // YYYY-MM-DD inclusive
  toDate?: string;   // YYYY-MM-DD inclusive
  /** Include voided entries (default: false). */
  includeVoided?: boolean;
}

/** Chart-of-Accounts block. */
function buildAccountsBlock(accounts: GLAccount[]): string {
  const header = ["!ACCNT", "NAME", "ACCNTTYPE", "DESC", "ACCNUM"].join(TAB);
  const rows = accounts.filter(a => a.active).map(a => [
    "ACCNT",
    accountFullName(a),
    iifAccountType(a),
    cleanCell(a.description ?? ""),
    cleanCell(a.number),
  ].join(TAB));
  return [header, ...rows, ""].join(CRLF);
}

/** Customer list (deduped from JE customerIds). */
function buildCustomerBlock(journalEntries: JournalEntry[]): string {
  const customers = new Set<string>();
  for (const je of journalEntries) {
    for (const l of je.lines) {
      if (l.customerId) customers.add(l.customerId);
    }
  }
  if (customers.size === 0) return "";
  const header = ["!CUST", "NAME", "BADDR1"].join(TAB);
  const rows = Array.from(customers).sort().map(cid => {
    const display = cid.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    return ["CUST", cleanCell(display), ""].join(TAB);
  });
  return [header, ...rows, ""].join(CRLF);
}

/** Vendor list. */
function buildVendorBlock(vendors: Vendor[]): string {
  const header = ["!VEND", "NAME", "VTYPE", "PHONE1", "EMAIL"].join(TAB);
  const rows = vendors.filter(v => v.active).map(v => [
    "VEND",
    cleanCell(v.vendorName),
    v.is1099Eligible ? "1099" : "",
    cleanCell(v.phone),
    cleanCell(v.email),
  ].join(TAB));
  return [header, ...rows, ""].join(CRLF);
}

/** General Journal transactions block. */
function buildJournalBlock(
  journalEntries: JournalEntry[],
  accountById: Map<string, GLAccount>,
  vendorNameById: Map<string, string>,
): string {
  const trnsHeader = [
    "!TRNS", "TRNSID", "TRNSTYPE", "DATE", "ACCNT", "NAME", "CLASS",
    "AMOUNT", "DOCNUM", "MEMO",
  ].join(TAB);
  const splHeader = [
    "!SPL", "SPLID", "TRNSTYPE", "DATE", "ACCNT", "NAME", "CLASS",
    "AMOUNT", "DOCNUM", "MEMO",
  ].join(TAB);
  const endHeader = "!ENDTRNS";

  const blocks: string[] = [trnsHeader, splHeader, endHeader];

  for (const je of journalEntries) {
    if (je.lines.length === 0) continue;
    const [firstLine, ...rest] = je.lines;
    const firstAcc = accountById.get(firstLine.accountId);
    if (!firstAcc) continue;

    const dateStr = iifDate(je.postingDate);
    const docNum = cleanCell(je.number);
    const resolveName = (l: typeof firstLine): string => {
      if (l.customerId) return cleanCell(l.customerId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()));
      if (l.vendorId) return cleanCell(vendorNameById.get(l.vendorId) ?? l.vendorId);
      return "";
    };

    blocks.push([
      "TRNS",
      cleanCell(je.id),
      "GENERAL JOURNAL",
      dateStr,
      accountFullName(firstAcc),
      resolveName(firstLine),
      cleanCell(firstLine.tradeType ?? ""),
      iifAmount(firstLine.amount, firstLine.side),
      docNum,
      cleanCell(je.description),
    ].join(TAB));

    for (const l of rest) {
      const acc = accountById.get(l.accountId);
      if (!acc) continue;
      blocks.push([
        "SPL",
        cleanCell(l.id),
        "GENERAL JOURNAL",
        dateStr,
        accountFullName(acc),
        resolveName(l),
        cleanCell(l.tradeType ?? ""),
        iifAmount(l.amount, l.side),
        docNum,
        cleanCell(l.memo ?? ""),
      ].join(TAB));
    }

    blocks.push("ENDTRNS");
  }

  return blocks.join(CRLF) + CRLF;
}

export function buildIifExport(args: {
  accounts: GLAccount[];
  journalEntries: JournalEntry[];
  vendors: Vendor[];
  options?: IifExportOptions;
}): string {
  const { accounts, journalEntries, vendors, options = {} } = args;
  const accountById = new Map(accounts.map(a => [a.id, a]));
  const vendorNameById = new Map(vendors.map(v => [v.vendorId, v.vendorName]));

  const filteredJEs = journalEntries.filter(je => {
    if (!options.includeVoided && je.status === "void") return false;
    if (je.status === "draft") return false;
    return inRange(je.postingDate, options.fromDate, options.toDate);
  });

  return [
    buildAccountsBlock(accounts),
    buildCustomerBlock(filteredJEs),
    buildVendorBlock(vendors),
    buildJournalBlock(filteredJEs, accountById, vendorNameById),
  ].filter(Boolean).join(CRLF);
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV Pack
// ─────────────────────────────────────────────────────────────────────────────

export interface CsvFile {
  filename: string;
  contents: string;
}

export interface CsvExportOptions extends IifExportOptions {}

export function buildCsvPack(args: {
  accounts: GLAccount[];
  journalEntries: JournalEntry[];
  vendors: Vendor[];
  bills: Bill[];
  payments: BillPayment[];
  options?: CsvExportOptions;
}): CsvFile[] {
  const { accounts, journalEntries, vendors, bills, payments, options = {} } = args;
  const accountById = new Map(accounts.map(a => [a.id, a]));
  const vendorNameById = new Map(vendors.map(v => [v.vendorId, v.vendorName]));

  const filteredJEs = journalEntries.filter(je => {
    if (!options.includeVoided && je.status === "void") return false;
    if (je.status === "draft") return false;
    return inRange(je.postingDate, options.fromDate, options.toDate);
  });

  // 1. Chart of Accounts
  const accountsCsv = [
    ["Number", "Name", "Type", "Subtype", "Normal Balance", "Parent", "Active"].map(csvCell).join(","),
    ...accounts.map(a => [
      a.number, a.name, a.type, a.subtype, a.normalBalance,
      a.parentId ?? "", a.active ? "Y" : "N",
    ].map(csvCell).join(",")),
  ].join("\n");

  // 2. Journal Entries — one row per line for accountant readability
  const jeCsv = [
    ["JE#", "Date", "Period", "Source", "Status", "Description",
      "Account #", "Account Name", "Debit", "Credit",
      "Customer", "Vendor", "Job", "Trade", "Memo"].map(csvCell).join(","),
    ...filteredJEs.flatMap(je => je.lines.map(l => {
      const acc = accountById.get(l.accountId);
      return [
        je.number, je.postingDate, je.period, je.source, je.status, je.description,
        acc?.number ?? l.accountId, acc?.name ?? "",
        l.side === "debit" ? l.amount.toFixed(2) : "",
        l.side === "credit" ? l.amount.toFixed(2) : "",
        l.customerId ?? "", l.vendorId ? (vendorNameById.get(l.vendorId) ?? l.vendorId) : "",
        l.jobId ?? "", l.tradeType ?? "", l.memo ?? "",
      ].map(csvCell).join(",");
    })),
  ].join("\n");

  // 3. Customers
  const customerIds = new Set<string>();
  for (const je of filteredJEs) for (const l of je.lines) if (l.customerId) customerIds.add(l.customerId);
  const customersCsv = [
    ["Customer ID", "Display Name"].map(csvCell).join(","),
    ...Array.from(customerIds).sort().map(cid => [
      cid,
      cid.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    ].map(csvCell).join(",")),
  ].join("\n");

  // 4. Vendors (with 1099 fields)
  const vendorsCsv = [
    ["Vendor ID", "Name", "Contact", "Phone", "Email", "1099 Eligible",
      "TIN Last4", "W-9 Status", "W-9 Received", "Active"].map(csvCell).join(","),
    ...vendors.map(v => [
      v.vendorId, v.vendorName, v.contactName, v.phone, v.email,
      v.is1099Eligible ? "Y" : "N",
      v.taxIdLast4 ?? "", v.w9Status ?? "not_requested", v.w9ReceivedAt ?? "",
      v.active ? "Y" : "N",
    ].map(csvCell).join(",")),
  ].join("\n");

  // 5. Bills
  const billsInRange = bills.filter(b => inRange(b.billDate, options.fromDate, options.toDate));
  const billsCsv = [
    ["Bill ID", "Invoice #", "Vendor", "Bill Date", "Due Date", "Total",
      "Paid", "Open Balance", "Status", "JE#", "Notes"].map(csvCell).join(","),
    ...billsInRange.map(b => [
      b.id, b.invoiceNumber, b.vendorName, b.billDate, b.dueDate,
      b.total.toFixed(2), b.amountPaid.toFixed(2), (b.total - b.amountPaid).toFixed(2),
      b.status,
      filteredJEs.find(je => je.id === b.postedJournalEntryId)?.number ?? "",
      b.notes ?? "",
    ].map(csvCell).join(",")),
  ].join("\n");

  // 6. Bill Payments
  const paymentsInRange = payments.filter(p => inRange(p.paidAt.slice(0, 10), options.fromDate, options.toDate));
  const paymentsCsv = [
    ["Payment ID", "Bill ID", "Vendor", "Paid Date", "Amount", "Method",
      "From Account", "Reference", "JE#"].map(csvCell).join(","),
    ...paymentsInRange.map(p => {
      const bill = bills.find(b => b.id === p.billId);
      return [
        p.id, p.billId, bill?.vendorName ?? "", p.paidAt, p.amount.toFixed(2), p.method,
        p.fromAccountId, p.reference ?? "",
        filteredJEs.find(je => je.id === p.postedJournalEntryId)?.number ?? "",
      ].map(csvCell).join(",");
    }),
  ].join("\n");

  // 7. Trial balance for the range (period-snapshot, lightweight)
  const tbTotals = new Map<string, { debit: number; credit: number }>();
  for (const je of filteredJEs) {
    for (const l of je.lines) {
      const t = tbTotals.get(l.accountId) ?? { debit: 0, credit: 0 };
      if (l.side === "debit") t.debit += l.amount; else t.credit += l.amount;
      tbTotals.set(l.accountId, t);
    }
  }
  const tbCsv = [
    ["Account #", "Account Name", "Type", "Debits", "Credits", "Net"].map(csvCell).join(","),
    ...accounts.map(a => {
      const t = tbTotals.get(a.id) ?? { debit: 0, credit: 0 };
      const net = a.normalBalance === "debit" ? t.debit - t.credit : t.credit - t.debit;
      if (t.debit === 0 && t.credit === 0) return null;
      return [a.number, a.name, a.type, t.debit.toFixed(2), t.credit.toFixed(2), net.toFixed(2)]
        .map(csvCell).join(",");
    }).filter((x): x is string => x !== null),
  ].join("\n");

  return [
    { filename: "01_chart_of_accounts.csv", contents: accountsCsv },
    { filename: "02_journal_entries.csv", contents: jeCsv },
    { filename: "03_trial_balance.csv", contents: tbCsv },
    { filename: "04_customers.csv", contents: customersCsv },
    { filename: "05_vendors.csv", contents: vendorsCsv },
    { filename: "06_bills.csv", contents: billsCsv },
    { filename: "07_bill_payments.csv", contents: paymentsCsv },
  ];
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary for the UI preview
// ─────────────────────────────────────────────────────────────────────────────

export interface ExportSummary {
  journalEntryCount: number;
  journalLineCount: number;
  totalDebits: number;
  totalCredits: number;
  balanced: boolean;
  billCount: number;
  paymentCount: number;
  vendorCount: number;
  customerCount: number;
  voidedCount: number;
}

export function summarizeExport(args: {
  journalEntries: JournalEntry[];
  bills: Bill[];
  payments: BillPayment[];
  vendors: Vendor[];
  options?: IifExportOptions;
}): ExportSummary {
  const { journalEntries, bills, payments, vendors, options = {} } = args;
  const customerIds = new Set<string>();
  let lineCount = 0, dr = 0, cr = 0, voided = 0;

  const visible = journalEntries.filter(je => {
    if (je.status === "draft") return false;
    if (!inRange(je.postingDate, options.fromDate, options.toDate)) return false;
    if (je.status === "void") { voided += 1; return options.includeVoided === true; }
    return true;
  });

  for (const je of visible) {
    for (const l of je.lines) {
      lineCount += 1;
      if (l.side === "debit") dr += l.amount; else cr += l.amount;
      if (l.customerId) customerIds.add(l.customerId);
    }
  }

  return {
    journalEntryCount: visible.length,
    journalLineCount: lineCount,
    totalDebits: dr,
    totalCredits: cr,
    balanced: Math.round(dr * 100) === Math.round(cr * 100),
    billCount: bills.filter(b => inRange(b.billDate, options.fromDate, options.toDate)).length,
    paymentCount: payments.filter(p => inRange(p.paidAt.slice(0, 10), options.fromDate, options.toDate)).length,
    vendorCount: vendors.filter(v => v.active).length,
    customerCount: customerIds.size,
    voidedCount: voided,
  };
}
